/**
 * The bridge from the name a decision was made under to what that unit is
 * today — "Platform Eng (now Core Infrastructure)".
 *
 * Frozen artifacts keep the structure they were decided under
 * (cycle_reports.structure_snapshot, mig 727). `resolve_structure_nodes`
 * (mig 733) says what each of those node ids means now. This turns the pair
 * into words. A unit that was retired with nothing replacing it says so
 * plainly: unmapped is a legitimate answer, not a gap to paper over.
 *
 * Spec: docs/9-exploration/PRD-structure-versioning-and-transitions.md FR-6.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { HierarchyKind } from './hierarchyTypes';

export type StructureNodeStatus = 'continues' | 'merged_into' | 'split_into' | 'retired';

export interface ResolvedStructureNode {
  nodeId: string;
  status: StructureNodeStatus;
  current: Array<{ id: string; name: string; pathNames: string[]; weight: number | null }>;
  steps: Array<{ name: string | null; disposition: string; inVersion: string; label: string | null; at: string | null }>;
}

/** What to show after the historical name, or null when nothing changed. */
export function structureBridgeLabel(thenName: string, resolved: ResolvedStructureNode | null | undefined): string | null {
  if (!resolved) return null;
  const names = resolved.current.map((c) => c.name);
  switch (resolved.status) {
    case 'retired':
      return 'since retired — nothing replaced it';
    case 'merged_into':
      return names.length > 0 ? `now part of ${names[0]}` : 'merged into a unit that has since been retired';
    case 'split_into':
      return names.length > 0 ? `since split into ${joinNames(names)}` : 'split into units that have since been retired';
    case 'continues':
    default:
      return names[0] && names[0] !== thenName ? `now ${names[0]}` : null;
  }
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "Acme › Platform Eng" moved to "Acme › Technology › Platform Eng" → the new path, else null. */
export function structureBridgeMovedTo(thenPath: string[], resolved: ResolvedStructureNode | null | undefined): string | null {
  if (!resolved || resolved.status !== 'continues' || resolved.current.length !== 1) return null;
  const nowParents = resolved.current[0].pathNames.slice(0, -1);
  const thenParents = thenPath.slice(0, -1);
  if (nowParents.length === thenParents.length && nowParents.every((n, i) => n === thenParents[i])) return null;
  return nowParents.join(' › ') || 'the top level';
}

type Rec = Record<string, unknown>;

export async function resolveStructureNodes(kind: HierarchyKind, nodeIds: string[]): Promise<Map<string, ResolvedStructureNode | null>> {
  const out = new Map<string, ResolvedStructureNode | null>();
  const ids = [...new Set(nodeIds)];
  if (ids.length === 0) return out;
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('resolve_structure_nodes', { p_kind: kind, p_node_ids: ids });
  if (error) throw new Error(`Failed to resolve structure: ${error.message}`);
  for (const [id, raw] of Object.entries((data ?? {}) as Record<string, Rec | null>)) {
    if (!raw) {
      out.set(id, null);
      continue;
    }
    out.set(id, {
      nodeId: id,
      status: raw.status as StructureNodeStatus,
      current: (Array.isArray(raw.current) ? (raw.current as Rec[]) : []).map((c) => ({
        id: String(c.id),
        name: String(c.name ?? ''),
        pathNames: Array.isArray(c.path_names) ? (c.path_names as string[]) : [],
        weight: typeof c.weight === 'number' ? c.weight : null,
      })),
      steps: (Array.isArray(raw.steps) ? (raw.steps as Rec[]) : []).map((s) => ({
        name: (s.name as string | null) ?? null,
        disposition: String(s.disposition),
        inVersion: String(s.in_version),
        label: (s.label as string | null) ?? null,
        at: (s.at as string | null) ?? null,
      })),
    });
  }
  return out;
}
